import { useState, useEffect } from 'react';
import api from '../api/client';

// 시스템관리자 전용 사이드바 편집 모달
// path / menu / badgeKey 는 편집 불가(코드와 결합). title / label / icon / 순서만 편집.
export default function SidebarEditorModal({ open, onClose, initialConfig, onSaved, initialLogo, onLogoSaved }) {
  const defaultLogo = () => ({ type: 'text', line1: '', line1_color: '#60a5fa', line2: '', line2_color: '#ffffff' });
  const [config, setConfig] = useState(initialConfig || []);
  const [logo, setLogo] = useState(initialLogo ? { ...defaultLogo(), ...initialLogo } : defaultLogo());
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (open) {
      setConfig(JSON.parse(JSON.stringify(initialConfig || [])));
      // 과거 데이터(type:'default' 또는 단일 text 필드) → 새 두-줄 구조로 마이그레이션
      const incoming = initialLogo ? JSON.parse(JSON.stringify(initialLogo)) : {};
      if (!incoming.type || incoming.type === 'default') {
        setLogo({ ...defaultLogo(), line1: incoming.text || '' });
      } else if (incoming.type === 'text') {
        setLogo({ ...defaultLogo(), ...incoming, line1: incoming.line1 || incoming.text || '' });
      } else {
        setLogo({ ...defaultLogo(), ...incoming });
      }
    }
  }, [open, initialConfig, initialLogo]);

  if (!open) return null;

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const apiBase = (process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:4000`).replace(/\/$/, '');
      const res = await fetch(`${apiBase}/api/upload/logo`, { method:'POST', headers:{ Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '업로드 실패');
      setLogo({ type: 'image', image_url: data.url });
    } catch (e) {
      alert(e.message);
    } finally { setUploadingLogo(false); }
  };

  const updateSectionTitle = (si, title) => {
    setConfig(c => c.map((s, i) => i === si ? { ...s, title } : s));
  };
  const updateItem = (si, ii, patch) => {
    setConfig(c => c.map((s, i) => i === si ? { ...s, items: s.items.map((it, j) => j === ii ? { ...it, ...patch } : it) } : s));
  };
  const moveSection = (si, dir) => {
    const ni = si + dir;
    if (ni < 0 || ni >= config.length) return;
    const next = [...config];
    [next[si], next[ni]] = [next[ni], next[si]];
    setConfig(next);
  };
  const moveItem = (si, ii, dir) => {
    const items = config[si].items;
    const ni = ii + dir;
    if (ni < 0 || ni >= items.length) return;
    const newItems = [...items];
    [newItems[ii], newItems[ni]] = [newItems[ni], newItems[ii]];
    setConfig(c => c.map((s, i) => i === si ? { ...s, items: newItems } : s));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/sidebar_config', { value: config });
      // 로고: 텍스트 모드에서 두 줄 모두 비어있거나 이미지 모드에서 URL 이 없으면 삭제 처리 (= 로고 미설정)
      const empty = (logo.type === 'text' && !logo.line1 && !logo.line2) ||
                    (logo.type === 'image' && !logo.image_url);
      if (empty) {
        await api.del('/settings/sidebar_logo').catch(()=>{});
        onLogoSaved && onLogoSaved(null);
      } else {
        await api.put('/settings/sidebar_logo', { value: logo });
        onLogoSaved && onLogoSaved(logo);
      }
      onSaved && onSaved(config);
      onClose();
    } catch (e) {
      alert('저장 실패: ' + (e.message || ''));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('기본값으로 복원하시겠습니까? 사용자 정의 설정은 모두 사라집니다.')) return;
    setSaving(true);
    try {
      await api.del('/settings/sidebar_config');
      await api.del('/settings/sidebar_logo').catch(()=>{});
      onLogoSaved && onLogoSaved(null);
      onSaved && onSaved(null); // null → Sidebar 가 default 로 폴백
      onClose();
    } catch (e) {
      alert('복원 실패: ' + (e.message || ''));
    } finally { setSaving(false); }
  };

  const arrowBtn = (disabled, onClick, label) => (
    <button onClick={onClick} disabled={disabled}
      style={{ width:24, height:24, padding:0, fontSize:11, border:'1px solid #e2e8f0', background:disabled?'#f8fafc':'#fff', borderRadius:4, cursor:disabled?'default':'pointer', color:disabled?'#cbd5e1':'#475569' }}>
      {label}
    </button>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',color:'#222',borderRadius:14,padding:24,width:600,maxHeight:'85vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
          <span>사이드바 메뉴 편집</span>
          <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={onClose}>✕</span>
        </div>
        <div style={{background:'#f8fafc',borderRadius:6,padding:'8px 12px',fontSize:11,color:'#64748b',marginBottom:14,lineHeight:1.7}}>
          로고, 섹션 제목, 메뉴 이름, 아이콘과 순서를 편집할 수 있습니다.<br/>
          경로(path)·권한키(menu)는 코드와 결합되어 변경 불가합니다.
        </div>

        {/* 로고 편집 */}
        <div style={{border:'1px solid #e2e8f0',borderRadius:8,padding:12,background:'#fafbfc',marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>로고</div>
          <div style={{display:'flex',gap:14,marginBottom:10,fontSize:12}}>
            {[
              {val:'text',  lab:'텍스트'},
              {val:'image', lab:'이미지 (PNG/SVG)'},
            ].map(o => (
              <label key={o.val} style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer'}}>
                <input type="radio" name="logoType" checked={logo.type === o.val} onChange={()=>setLogo({ ...logo, type: o.val })} />
                {o.lab}
              </label>
            ))}
          </div>
          {logo.type === 'text' && (
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <span style={{fontSize:11,color:'#94a3b8',width:50}}>1번째 줄</span>
                <input className="form-input" style={{flex:1,padding:'6px 10px',fontSize:13}}
                  value={logo.line1 || ''} onChange={e=>setLogo({...logo, line1:e.target.value})}
                  placeholder="예: SOOSAN" maxLength={40} />
                <input type="color" value={logo.line1_color || '#60a5fa'}
                  onChange={e=>setLogo({...logo, line1_color:e.target.value})}
                  title="1번째 줄 색상"
                  style={{width:34,height:30,padding:0,border:'1px solid #e2e8f0',borderRadius:4,cursor:'pointer',background:'#fff'}} />
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <span style={{fontSize:11,color:'#94a3b8',width:50}}>2번째 줄</span>
                <input className="form-input" style={{flex:1,padding:'6px 10px',fontSize:13}}
                  value={logo.line2 || ''} onChange={e=>setLogo({...logo, line2:e.target.value})}
                  placeholder="(선택) 예: ERP" maxLength={40} />
                <input type="color" value={logo.line2_color || '#ffffff'}
                  onChange={e=>setLogo({...logo, line2_color:e.target.value})}
                  title="2번째 줄 색상"
                  style={{width:34,height:30,padding:0,border:'1px solid #e2e8f0',borderRadius:4,cursor:'pointer',background:'#fff'}} />
              </div>
            </div>
          )}
          {logo.type === 'image' && (
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              {logo.image_url ? (
                <img src={logo.image_url} alt="logo" height={32} style={{height:32,width:'auto',maxWidth:160,background:'#1e293b',padding:4,borderRadius:4,display:'inline-block'}} />
              ) : <span style={{fontSize:11,color:'#94a3b8'}}>이미지를 업로드하세요</span>}
              <input type="file" accept=".png,.svg,image/png,image/svg+xml"
                onChange={e=>{ const f=e.target.files?.[0]; if (f) uploadLogo(f); e.target.value=''; }}
                disabled={uploadingLogo}
                style={{fontSize:12}} />
              {uploadingLogo && <span style={{fontSize:11,color:'#64748b'}}>업로드 중…</span>}
            </div>
          )}
          <div style={{fontSize:10,color:'#94a3b8',marginTop:6}}>※ 사이드바 배경이 어두우므로 밝은 색 로고를 권장합니다. 이미지 최대 2MB.</div>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {config.map((section, si) => (
            <div key={si} style={{border:'1px solid #e2e8f0',borderRadius:8,padding:12,background:'#fafbfc'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <span style={{fontSize:11,color:'#94a3b8',width:50}}>섹션</span>
                <input className="form-input" style={{flex:1,padding:'5px 8px',fontSize:13,fontWeight:600}}
                  value={section.title || ''} onChange={e=>updateSectionTitle(si, e.target.value)} />
                <div style={{display:'flex',gap:2}}>
                  {arrowBtn(si === 0, ()=>moveSection(si,-1), '↑')}
                  {arrowBtn(si === config.length-1, ()=>moveSection(si,1), '↓')}
                </div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:4,paddingLeft:8,borderLeft:'2px solid #e2e8f0'}}>
                {section.items.map((item, ii) => {
                  const enabled = item.hidden !== true;
                  return (
                    <div key={item.path} style={{display:'flex',alignItems:'center',gap:6, opacity: enabled ? 1 : 0.45}}>
                      <input className="form-input" style={{width:40,padding:'4px 6px',fontSize:13,textAlign:'center'}}
                        value={item.icon || ''} onChange={e=>updateItem(si,ii,{icon:e.target.value})}
                        placeholder="🎁" maxLength={4} title="아이콘 (이모지 또는 문자)" />
                      <input className="form-input" style={{flex:1,padding:'4px 8px',fontSize:12, textDecoration: enabled ? 'none' : 'line-through'}}
                        value={item.label || ''} onChange={e=>updateItem(si,ii,{label:e.target.value})}
                        placeholder="메뉴 이름" />
                      <span style={{fontSize:10,color:'#94a3b8',minWidth:80,fontFamily:'monospace'}}>{item.path}</span>
                      <button onClick={()=>updateItem(si,ii,{hidden: !item.hidden})}
                        title={enabled ? '사이드바에서 숨김' : '사이드바에 표시'}
                        style={{ width:34, height:18, padding:0, border:'none', borderRadius:9, cursor:'pointer', position:'relative', background: enabled ? '#3b82f6' : '#cbd5e1', transition:'background 0.15s' }}>
                        <span style={{ position:'absolute', top:2, left: enabled ? 18 : 2, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.15s' }} />
                      </button>
                      <div style={{display:'flex',gap:2}}>
                        {arrowBtn(ii === 0, ()=>moveItem(si,ii,-1), '↑')}
                        {arrowBtn(ii === section.items.length-1, ()=>moveItem(si,ii,1), '↓')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,paddingTop:12,borderTop:'1px solid #e2e8f0'}}>
          <button className="btn btn-outline btn-sm" style={{color:'#dc2626',borderColor:'#fecaca'}} onClick={handleReset} disabled={saving}>기본값 복원</button>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-outline" onClick={onClose} disabled={saving}>취소</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'저장 중...':'저장'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
